import {DatePicker, Input} from 'antd';
import {Rule} from 'antd/lib/form';
import dayjs, {Dayjs} from 'dayjs';
import {PropsWithChildren, useCallback, useMemo} from 'react';

import {AdditionalInfo} from '../../../../../../layout/additional-info/additional-info';
import {Locale} from '../../../../../../provider/locale/locale';
import {useLocale} from '../../../../../../provider/locale/locale-hook';
import {HelpLinkFeatureFlagEnum} from '../../../../../../service/feature-flags/feature-flag-const';
import {useFeatureFlag} from '../../../../../../service/feature-flags/use-feature-flag';
import {useFormRules} from '../../../../../../service/form/form-rules-hook';
import {
    YandexStoryFormFieldsEnum,
    YandexStoryFormType,
    YandexStoryTypeEnum,
} from '../../../../../../service/yandex-stories/yandex-stories-type';
import {Form, FormInstance} from '../../../../../../typings/antd';
import {
    YANDEX_STORY_DATE_TIME_FORMAT,
    YANDEX_STORY_MAX_NAME_LENGTH,
    YANDEX_STORY_TIME_FORMAT,
} from '../../../yandex-story-form-helper';

import {YandexStoryCoverField} from './yandex-story-cover-field';
import {YandexStoryToggleCard} from './yandex-story-toggle-card';
import * as styles from './yandex-story-settings-panel.scss';

type PropsType = PropsWithChildren<{
    formInstance: FormInstance<YandexStoryFormType>;
    storyType: YandexStoryTypeEnum;
    // Until the first slide is uploaded the panel shows the story name only, as in the design.
    hasSlides: boolean;
}>;

// Right-hand column of the story step: the story name, the publication range and — as children —
// the controls of the slide that is currently previewed.
export function YandexStorySettingsPanel(props: PropsType): JSX.Element {
    const {formInstance, storyType, hasSlides, children} = props;

    const {getLocalizedString} = useLocale();
    const {maxLengthFieldRule, requiredFieldTrimmedTextRule} = useFormRules();

    // Publish/delete scheduling shares the publication-catalog rollout flag.
    const isPublicationCatalogEnabled = useFeatureFlag(HelpLinkFeatureFlagEnum.publicationCatalog);

    const publicationRange = Form.useWatch(YandexStoryFormFieldsEnum.PublicationRange, {
        form: formInstance,
        preserve: true,
    });

    // Mirrors `validate_story_scheduling` on the backend: the deletion date has to be ahead of both
    // now and the publish date. A publish date in the past is allowed — the story is then sent as
    // `in_progress` instead of `planned`, which simply means "publish now", and the backend only
    // requires a future publish date for planned stories.
    const publicationRangeRule = useMemo<Rule>(
        () => ({
            validator: (_rule: unknown, value: [Dayjs | null, Dayjs | null] | null): Promise<void> => {
                const [publishAt, deleteAt] = value ?? [null, null];

                if (deleteAt?.isBefore(dayjs())) {
                    return Promise.reject(
                        new Error(
                            getLocalizedString('YANDEX_STORY_FORM__CONFIRMATION_STEP__VALIDATION__DATE_IN_FUTURE')
                        )
                    );
                }

                if (publishAt && deleteAt && !deleteAt.isAfter(publishAt)) {
                    return Promise.reject(
                        new Error(
                            getLocalizedString('YANDEX_STORY_FORM__CONFIRMATION_STEP__VALIDATION__DELETE_AFTER_PUBLISH')
                        )
                    );
                }

                return Promise.resolve();
            },
        }),
        [getLocalizedString]
    );

    // Days that are wholly in the past can hold neither end of the range, so the picker hides them.
    // Today stays available: a start earlier today is the "publish now" case.
    const isDateDisabled = useCallback((current: Dayjs) => current.isBefore(dayjs(), 'day'), []);

    const handleRangeToggle = useCallback(
        (isEnabled: boolean) => {
            formInstance.setFieldValue(
                YandexStoryFormFieldsEnum.PublicationRange,
                isEnabled ? [dayjs().add(1, 'hour'), null] : null
            );
        },
        [formInstance]
    );

    return (
        <div className={styles.YandexStorySettingsPanel}>
            <Form.Item<YandexStoryFormType>
                label={
                    storyType === YandexStoryTypeEnum.Navigation ? (
                        <AdditionalInfo
                            leftNode={<Locale stringKey="YANDEX_STORY_FORM__CONFIRMATION_STEP__STORY_NAME__LABEL" />}
                        >
                            <Locale stringKey="YANDEX_STORY__HOW_TO_FIND__NAME__TOOLTIP" />
                        </AdditionalInfo>
                    ) : (
                        <Locale stringKey="YANDEX_STORY_FORM__CONFIRMATION_STEP__STORY_NAME__LABEL" />
                    )
                }
                name={YandexStoryFormFieldsEnum.Name}
                required
                rules={[requiredFieldTrimmedTextRule, maxLengthFieldRule(YANDEX_STORY_MAX_NAME_LENGTH)]}
            >
                <Input
                    disabled={storyType === YandexStoryTypeEnum.Navigation}
                    maxLength={YANDEX_STORY_MAX_NAME_LENGTH}
                    placeholder={getLocalizedString('YANDEX_STORY_FORM__CONFIRMATION_STEP__STORY_NAME__PLACEHOLDER')}
                    showCount
                />
            </Form.Item>

            {hasSlides && <YandexStoryCoverField formInstance={formInstance} />}

            {isPublicationCatalogEnabled && hasSlides && (
                <YandexStoryToggleCard
                    isEnabled={Boolean(publicationRange)}
                    labelLangKey="YANDEX_STORY_FORM__PUBLICATION_RANGE__LABEL"
                    onToggle={handleRangeToggle}
                >
                    <Form.Item<YandexStoryFormType>
                        name={YandexStoryFormFieldsEnum.PublicationRange}
                        rules={[publicationRangeRule]}
                    >
                        <DatePicker.RangePicker
                            className={styles.YandexStorySettingsPanel_rangePicker}
                            disabledDate={isDateDisabled}
                            format={YANDEX_STORY_DATE_TIME_FORMAT}
                            placeholder={[
                                getLocalizedString('YANDEX_STORY_FORM__CONFIRMATION_STEP__PUBLISH_AT'),
                                getLocalizedString('YANDEX_STORY_FORM__CONFIRMATION_STEP__DELETE_AT'),
                            ]}
                            showTime={{format: YANDEX_STORY_TIME_FORMAT}}
                        />
                    </Form.Item>
                </YandexStoryToggleCard>
            )}

            {children}
        </div>
    );
}
